/**
 * ScrollButtons — floating up/down navigation buttons for the chat messages area.
 *
 * Visibility is controlled via inline opacity style so CSS transitions work
 * smoothly. The buttons remain in the DOM at all times. When hidden, pointer
 * events are disabled via the scroll-btn CSS class (opacity 0 + pointer-events
 * none); when visible, the scroll-btn--visible modifier re-enables both.
 *
 * Hiding strategy: inline `opacity: 0` (visible to isVisuallyHidden helper in
 * tests, and does NOT suppress accessible name computation unlike aria-hidden).
 */

interface ScrollButtonsProps {
	showScrollTop: boolean;
	showScrollBottom: boolean;
	onScrollTop: () => void;
	onScrollBottom: () => void;
}

export default function ScrollButtons({
	showScrollTop,
	showScrollBottom,
	onScrollTop,
	onScrollBottom,
}: ScrollButtonsProps) {
	return (
		<div className="scroll-buttons">
			<button
				type="button"
				className={`scroll-btn${showScrollTop ? " scroll-btn--visible" : ""}`}
				style={showScrollTop ? undefined : { opacity: 0 }}
				aria-label="Scroll to top"
				onClick={showScrollTop ? onScrollTop : undefined}
			>
				{/* Chevron up */}
				<svg
					width="16"
					height="16"
					viewBox="0 0 16 16"
					fill="none"
					aria-hidden="true"
					focusable="false"
				>
					<path
						d="M3 10.5L8 5.5L13 10.5"
						stroke="currentColor"
						strokeWidth="1.75"
						strokeLinecap="round"
						strokeLinejoin="round"
					/>
				</svg>
			</button>

			<button
				type="button"
				className={`scroll-btn${showScrollBottom ? " scroll-btn--visible" : ""}`}
				style={showScrollBottom ? undefined : { opacity: 0 }}
				aria-label="Scroll to bottom"
				onClick={showScrollBottom ? onScrollBottom : undefined}
			>
				{/* Chevron down */}
				<svg
					width="16"
					height="16"
					viewBox="0 0 16 16"
					fill="none"
					aria-hidden="true"
					focusable="false"
				>
					<path
						d="M3 5.5L8 10.5L13 5.5"
						stroke="currentColor"
						strokeWidth="1.75"
						strokeLinecap="round"
						strokeLinejoin="round"
					/>
				</svg>
			</button>
		</div>
	);
}
